import { ThemeProvider } from "@/lib/theme-provider";
import { BrowserRouter, Route, Routes } from "react-router";
import { Provider } from "react-redux";
import { store, useAppSelector } from "@/lib/store";
import { auth_routes } from "@/routes/auth";
import { user_routes } from "@/routes/user";
import { pmc_routes } from "@/routes/pmc";
import { vendor_routes } from "@/routes/vendor";
import { subs_routes } from "@/routes/subs";
import { individual_routes } from "@/routes/individual";
import { contact_routes } from "@/routes/contact";
import { contact_properties_routes } from "@/routes/contact-properties";
import { AuthGate } from "@/components/shared/auth/auth-gate.tsx";
import { AuthInit } from "@/components/shared/auth/auth-init.tsx";
import { organization_type } from "@/models/organization.model.ts";
import type { RouteScope } from "@/lib/config.slice.ts";
import { CONTACT_SCOPE } from "@/lib/property-contact-scope";
import { Toaster } from "@/components/ui/sonner";
import { messages_routes } from "@/routes/messages.tsx";
import { organization_routes } from "@/routes/organization";
import * as Sentry from "@sentry/react";
import { sentryEnabled } from "@/instrument";

const AppRoutes = sentryEnabled ? Sentry.withSentryReactRouterV7Routing(Routes) : Routes;

function Page() {
  const { routeScope } = useAppSelector((state) => state.config);

  function getRoutes(scope: RouteScope | null) {
    const allRoutes = [...auth_routes, ...user_routes, ...messages_routes];
    const orgRoutes = [...organization_routes, ...contact_properties_routes];

    if (scope === organization_type.pmc) return [...allRoutes, ...pmc_routes, ...orgRoutes];
    if (scope === organization_type.vendor) return [...allRoutes, ...vendor_routes, ...orgRoutes];
    if (scope === organization_type.subs) return [...allRoutes, ...subs_routes, ...orgRoutes];
    if (scope === organization_type.emp) return [...allRoutes, ...individual_routes, ...orgRoutes];
    if (scope === CONTACT_SCOPE) return [...auth_routes, ...user_routes, ...contact_routes];
    return [...allRoutes];
  }

  return (
    <BrowserRouter>
      <AuthGate>
        <AppRoutes>
          {getRoutes(routeScope).map((route, index) => (
            <Route key={index} {...route} />
          ))}
        </AppRoutes>
      </AuthGate>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <Sentry.ErrorBoundary
        fallback={({ eventId, resetError }) => (
          <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground max-w-md text-sm">
              We&apos;ve captured the error and can investigate it. Reload the app to recover.
            </p>
            {eventId ? <p className="text-muted-foreground text-xs">Reference: {eventId}</p> : null}
            <button
              className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium"
              onClick={() => {
                resetError();
                window.location.reload();
              }}
              type="button"
            >
              Reload app
            </button>
          </div>
        )}
      >
        <Provider store={store}>
          <AuthInit />
          <Toaster richColors closeButton position="top-right" />
          <Page />
        </Provider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  );
}
